// Fan profile Phase 2 — the device IS the account.
//
// Fans never sign up: this phone's localStorage holds their identity (the
// nickname/@ig they typed once) and the list of order ids it created. The
// profile sheet re-fetches live statuses for those ids on open, so nothing
// here ever goes stale-authoritative — storage only remembers WHICH orders
// are theirs, the server always says what state they're in.
//
// Everything that mutates fan-profile storage fires FAN_PROFILE_EVENT so the
// header chip (a different component tree) re-reads without prop plumbing.

export const NICKNAME_KEY = "rq_client_nickname";
export const INSTAGRAM_KEY = "rq_client_instagram";
const ORDERS_KEY = "rq_fan_orders";
const NEW_FLAG_KEY = "rq_fan_new";
// Registry cap — newest first. 50 covers months of heavy use; the batch
// status endpoint accepts the same maximum.
const MAX_TRACKED = 50;
const ORDER_ID_RE = /^[a-f0-9]{24}$/i;

export const FAN_PROFILE_EVENT = "rq-fan-profile-changed";

// Mirror of the server's POINTS_PER_REQUEST (lib/fanPoints.js): what one
// device request is WORTH pre-claim. The +100 played bonus reveals itself
// after sign-in — before that we only promise what this device can count.
export const POINTS_PER_DEVICE_REQUEST = 25;

const emit = () => {
  try {
    window.dispatchEvent(new Event(FAN_PROFILE_EVENT));
  } catch {}
};

/* ---------- identity (nickname / instagram) ---------- */

// localStorage-first with a sessionStorage fallback: pre-Phase-1 the identity
// lived in sessionStorage, so a fan mid-session on the old storage migrates
// seamlessly. Writes go to localStorage only; clears wipe both stores so a
// stale session value can't resurrect.
export const readIdentity = (key: string): string => {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

export const writeIdentity = (key: string, value: string) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  } catch {}
  emit();
};

// One free-text field covers both ("rockstar123 @insta") — same parser the
// request page always used, now shared with the profile sheet's editor.
export const parseGuestIdentity = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return { nickname: "", instagram: "" };
  const atEnd = trimmed.match(/(?:^|\s)@([a-zA-Z0-9._]{1,30})\s*$/);
  if (!atEnd) {
    const atOnly = trimmed.match(/^@([a-zA-Z0-9._]{1,30})$/);
    return atOnly
      ? { nickname: "", instagram: atOnly[1] }
      : { nickname: trimmed.slice(0, 50), instagram: "" };
  }
  return {
    nickname: trimmed.replace(atEnd[0], "").trim().slice(0, 50),
    instagram: atEnd[1],
  };
};

// What the header chip / sheet shows: typed nickname verbatim, else the
// @handle. (First-name-from-card fallback arrives with the Phase 3 email
// rail, which is what starts exposing customerName to the client.)
export const fanDisplayName = (): string => {
  const nick = readIdentity(NICKNAME_KEY);
  if (nick) return nick;
  const ig = readIdentity(INSTAGRAM_KEY);
  return ig ? `@${ig}` : "";
};

export const fanInitial = (name: string): string => {
  const first = Array.from(name.trim())[0];
  return first ? first.toUpperCase() : "";
};

/* ---------- order registry ---------- */

export type FanOrderRef = { id: string; ts: number };

export const listFanOrders = (): FanOrderRef[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(ORDERS_KEY) || "[]");
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (e: any): e is FanOrderRef =>
        e && typeof e.id === "string" && ORDER_ID_RE.test(e.id) && typeof e.ts === "number"
    );
  } catch {
    return [];
  }
};

// Upsert-to-head: the most recently touched request sorts first, and
// revisiting an old tracking link bumps it back up.
export const upsertFanOrder = (id: string) => {
  if (!ORDER_ID_RE.test(id)) return;
  try {
    let entries = listFanOrders().filter((e) => e.id !== id);
    entries.unshift({ id, ts: Date.now() });
    entries = entries.slice(0, MAX_TRACKED);
    localStorage.setItem(ORDERS_KEY, JSON.stringify(entries));
  } catch {}
  emit();
};

/* ---------- "something resolved" dot ---------- */

// Set when a tracked request leaves the banner (DJ accepted/declined/played);
// the header chip shows a small dot until the fan opens the sheet. Locked
// with founder decision: resolved chips clear from the banner and live here.
export const hasFanNew = (): boolean => {
  try {
    return localStorage.getItem(NEW_FLAG_KEY) === "1";
  } catch {
    return false;
  }
};

export const setFanNew = () => {
  try {
    localStorage.setItem(NEW_FLAG_KEY, "1");
  } catch {}
  emit();
};

export const clearFanNew = () => {
  try {
    localStorage.removeItem(NEW_FLAG_KEY);
  } catch {}
  emit();
};
